import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Play,
    Pause,
    Upload,
    Download,
    Volume2,
    VolumeX,
    Eye,
    X,
    Loader2,
    CheckCircle2,
    Film,
    Sparkles,
    Maximize,
    Pencil,
    Music2,
    Type,
    Wand2,
    ArrowLeftRight,
    Smile,
    MessageSquare,
    Gauge,
    Crop,
    RotateCw,
    SlidersHorizontal,
    Layers,
} from "lucide-react";
import Logo from "@/components/Logo";
import EffectsLibrary from "@/components/editor/EffectsLibrary";
import EffectControls from "@/components/editor/EffectControls";
import Timeline from "@/features/editor/Timeline";
import {
    FiltersPanel,
    TextPanel,
    StickersPanel,
    AudioPanel,
    SpeedPanel,
    TransitionsPanel,
    CaptionsPanel,
    EditPanel,
    ExportPanel,
    ADJ_DEFS,
} from "@/features/editor/Sheets";
import { drawOverlay, hitTestOverlay } from "@/lib/overlay";
import { createEngine } from "@/lib/glEngine";
import {
    FILTERS,
    SAMPLES,
    ASPECTS,
    DEFAULT_ADJ,
    EFFECTS as FX_LIB,
    combinedCss,
    drawProcessed,
    drawSplitDivider,
    fmtTime,
} from "@/lib/filters";
import { isEffectLocked } from "@/lib/effects";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = { ...DEFAULT_ADJ, exposure: 100, highlights: 0, shadows: 0, temperature: 0, tint: 0, sharpness: 0, fade: 0 };

const TOOLS = [
    { id: "edit", label: "Edit", icon: Pencil },
    { id: "audio", label: "Audio", icon: Music2 },
    { id: "text", label: "Text", icon: Type },
    { id: "effects", label: "Effects", icon: Sparkles },
    { id: "filters", label: "Filters", icon: Wand2 },
    { id: "transitions", label: "Transitions", icon: ArrowLeftRight },
    { id: "stickers", label: "Stickers", icon: Smile },
    { id: "captions", label: "Captions", icon: MessageSquare },
    { id: "speed", label: "Speed", icon: Gauge },
    { id: "crop", label: "Crop", icon: Crop },
    { id: "rotate", label: "Rotate", icon: RotateCw },
    { id: "adjust", label: "Adjust", icon: SlidersHorizontal },
    { id: "overlay", label: "Overlay", icon: Layers },
];

const SHEET_TITLES = {
    edit: "Edit",
    audio: "Audio",
    text: "Text",
    effects: "Effects",
    filters: "Filters & Adjust",
    transitions: "Transitions",
    stickers: "Stickers",
    captions: "Captions",
    speed: "Speed",
    crop: "Crop",
    rotate: "Rotate",
    adjust: "Adjust",
    overlay: "Overlay",
};

const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

function probeMedia(url, isVideo = true) {
    return new Promise((resolve) => {
        const el = document.createElement(isVideo ? "video" : "audio");
        el.preload = "metadata";
        el.onloadedmetadata = () => resolve(el.duration && isFinite(el.duration) ? el.duration : 1);
        el.onerror = () => resolve(1);
        el.src = url;
    });
}

function cumAt(list, idx) {
    let cum = 0;
    for (let i = 0; i < idx; i++) cum += list[i].trimEnd - list[i].trimStart;
    return cum;
}

function clipAt(list, p) {
    let cum = 0;
    for (let i = 0; i < list.length; i++) {
        const d = list[i].trimEnd - list[i].trimStart;
        if (p < cum + d - 0.001 || i === list.length - 1) {
            return { idx: i, clip: list[i], cum };
        }
        cum += d;
    }
    return null;
}

export default function EditorPro() {
    // project
    const [clips, setClips] = useState([]);
    const [selectedClipId, setSelectedClipId] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [speed, setSpeed] = useState(1);
    const [muted, setMuted] = useState(false);
    const [muteOriginal, setMuteOriginal] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [aspectId, setAspectId] = useState("9-16");
    const [zoom, setZoom] = useState(1);
    // grade
    const [filterId, setFilterId] = useState("none");
    const [filterIntensity, setFilterIntensity] = useState(100);
    const [adj, setAdj] = useState(DEFAULTS);
    // effect
    const [effect, setEffect] = useState(null);
    const [fxOpen, setFxOpen] = useState(false);
    // overlays
    const [texts, setTexts] = useState([]);
    const [selectedTextId, setSelectedTextId] = useState(null);
    // audio
    const [music, setMusic] = useState(null);
    // ui
    const [tool, setTool] = useState(null);
    const [thumbs, setThumbs] = useState({});
    const [fxBaseToken, setFxBaseToken] = useState(0);
    const [favEffects, setFavEffects] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("deo_fav_effects")) || [];
        } catch (e) {
            return [];
        }
    });
    const [recentEffects, setRecentEffects] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("deo_recent_effects")) || [];
        } catch (e) {
            return [];
        }
    });
    // export
    const [exportOpts, setExportOpts] = useState({ res: "720", fps: 30, format: "webm" });
    const [exportState, setExportState] = useState({ active: false, progress: 0, url: null, size: 0, error: null });
    // captions
    const [capBusy, setCapBusy] = useState(false);
    const [capError, setCapError] = useState(null);

    const aspect = ASPECTS.find((a) => a.id === aspectId);
    const totalDur = clips.reduce((a, c) => a + (c.trimEnd - c.trimStart), 0);
    const mp4Ok = useMemo(() => {
        try {
            return window.MediaRecorder && MediaRecorder.isTypeSupported("video/mp4");
        } catch (e) {
            return false;
        }
    }, []);

    // refs
    const videoRef = useRef(null);
    const audioRef = useRef(null);
    const fileInputRef = useRef(null);
    const audioFileInputRef = useRef(null);
    const stageRef = useRef(null);
    const gradeCanvasRef = useRef(document.createElement("canvas"));
    const glCanvasRef = useRef(null);
    const overlayCanvasRef = useRef(null);
    const recordCanvasRef = useRef(document.createElement("canvas"));
    const engineRef = useRef(null);
    const engineFailedRef = useRef(false);
    const displayRef = useRef("2d");
    const playheadRef = useRef({ t: 0, el: null });
    const timeLabelRef = useRef(null);
    const curClipRef = useRef({ idx: 0, url: null });
    const srcUrlRef = useRef(null);
    const playingRef = useRef(false);
    const exportingRef = useRef(false);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const audioGraphRef = useRef(null);
    const dragTextRef = useRef(null);
    const fxBaseRef = useRef(document.createElement("canvas"));
    const engine2Ref = useRef(null);

    // live mirror for the render loop
    const live = useRef({});
    const preset = FILTERS.find((f) => f.id === filterId);
    const advAdj =
        adj.exposure !== 100 ||
        adj.highlights !== 0 ||
        adj.shadows !== 0 ||
        adj.temperature !== 0 ||
        adj.tint !== 0 ||
        adj.fade !== 0 ||
        adj.sharpness !== 0;
    live.current = {
        clips,
        total: totalDur,
        speed,
        rotation,
        aspectId,
        css: combinedCss(preset, adj),
        fIntensity: filterIntensity / 100,
        vignette: adj.vignette,
        effect,
        texts,
        music,
        muteOriginal,
        muted,
        glOpts: {
            exposure: adj.exposure / 100,
            highlights: (adj.highlights / 100) * 0.3,
            shadows: (adj.shadows / 100) * 0.3,
            temp: (adj.temperature / 100) * 0.5,
            tint: (adj.tint / 100) * 0.5,
            fade: adj.fade / 100,
            sharp: (adj.sharpness / 100) * 1.5,
        },
        split: 100,
        holdOriginal: false,
    };
    const pxsRef = useRef(56);
    pxsRef.current = Math.round(56 * zoom);

    /* ---------------- clips ---------------- */

    const genClipThumbs = (clip) => {
        const tv = document.createElement("video");
        tv.muted = true;
        tv.playsInline = true;
        tv.preload = "auto";
        tv.src = clip.url;
        const c = document.createElement("canvas");
        c.width = 96;
        c.height = 128;
        const ctx = c.getContext("2d");
        const N = 6;
        let i = 0;
        const out = [];
        const next = () => {
            if (i >= N) {
                setClips((cs) => cs.map((x) => (x.id === clip.id ? { ...x, thumbs: out } : x)));
                return;
            }
            tv.onseeked = () => {
                const vw = tv.videoWidth;
                const vh = tv.videoHeight;
                if (vw) {
                    const s = Math.max(96 / vw, 128 / vh);
                    ctx.drawImage(tv, (96 - vw * s) / 2, (128 - vh * s) / 2, vw * s, vh * s);
                }
                out.push(c.toDataURL("image/jpeg", 0.5));
                i++;
                next();
            };
            tv.currentTime = clip.trimStart + ((clip.trimEnd - clip.trimStart) * (i + 0.5)) / N;
        };
        tv.onloadeddata = () => next();
        tv.load();
    };

    const addFiles = async (files) => {
        for (const file of files) {
            if (!file.type.startsWith("video/")) {
                toast.error(`${file.name || "That file"} is not a video (use MP4 or WebM).`);
                continue;
            }
            const url = URL.createObjectURL(file);
            const dur = await probeMedia(url);
            const clip = {
                id: uid(),
                url,
                own: true,
                name: file.name || "clip",
                srcDuration: dur,
                trimStart: 0,
                trimEnd: dur,
                transition: null,
                thumbs: [],
                file,
            };
            setClips((cs) => [...cs, clip]);
            genClipThumbs(clip);
            toast.success(`${clip.name} added`);
        }
    };

    const addSample = async (s) => {
        const dur = await probeMedia(s.url);
        const clip = {
            id: uid(),
            url: s.url,
            own: false,
            name: s.title,
            srcDuration: dur,
            trimStart: 0,
            trimEnd: dur,
            transition: null,
            thumbs: [],
        };
        setClips((cs) => [...cs, clip]);
        genClipThumbs(clip);
        toast.success(`${s.title} added to the timeline`);
    };

    const removeClip = (clipId) => {
        setClips((cs) => {
            const target = cs.find((c) => c.id === clipId);
            if (target && target.own) URL.revokeObjectURL(target.url);
            const rest = cs.filter((c) => c.id !== clipId);
            if (!rest.length) {
                playheadRef.current.t = 0;
                playingRef.current = false;
                setPlaying(false);
            }
            return rest;
        });
        if (selectedClipId === clipId) setSelectedClipId(null);
        toast.success("Clip deleted");
    };

    const duplicateClip = () => {
        const idx = clips.findIndex((c) => c.id === selectedClipId);
        if (idx < 0) {
            toast.info("Tap a clip on the timeline first.");
            return;
        }
        const copy = { ...clips[idx], id: uid(), thumbs: clips[idx].thumbs };
        setClips((cs) => [...cs.slice(0, idx + 1), copy, ...cs.slice(idx + 1)]);
        toast.success("Clip duplicated");
    };

    const splitAtPlayhead = () => {
        const P = playheadRef.current.t;
        const at = clipAt(clips, P);
        if (!at) return;
        const local = P - at.cum;
        const d = at.clip.trimEnd - at.clip.trimStart;
        if (local < 0.2 || local > d - 0.2) {
            toast.info("Move the playhead inside a clip to split it.");
            return;
        }
        const a = { ...at.clip, trimEnd: at.clip.trimStart + local };
        const b = { ...at.clip, id: uid(), trimStart: at.clip.trimStart + local, transition: null, thumbs: [] };
        genClipThumbs(b);
        setClips((cs) => [...cs.slice(0, at.idx), a, b, ...cs.slice(at.idx + 1)]);
        toast.success("Split — drag clips to reposition them");
    };

    const trimClip = (idx, side, dt, original) => {
        setClips((cs) =>
            cs.map((c, i) => {
                if (i !== idx) return c;
                let { trimStart, trimEnd } = original || c;
                if (side === "start") trimStart = Math.max(0, Math.min(trimStart + dt, trimEnd - 0.2));
                else trimEnd = Math.min(c.srcDuration, Math.max(trimEnd + dt, trimStart + 0.2));
                return { ...c, trimStart, trimEnd };
            })
        );
    };

    const reorderClip = (from, to) => {
        const j = Math.max(0, Math.min(clips.length - 1, to));
        if (from === j) return;
        setClips((cs) => {
            const copy = [...cs];
            const [m] = copy.splice(from, 1);
            copy.splice(j, 0, m);
            return copy;
        });
    };

    const setClipTransition = (id, label) => {
        const idx = clips.findIndex((c) => c.id === selectedClipId);
        const target = idx >= 0 ? idx : 0;
        setClips((cs) =>
            cs.map((c, i) =>
                i === target
                    ? { ...c, transition: id === "none" ? null : { id, label, dur: c.transition ? c.transition.dur : 0.8 } }
                    : c
            )
        );
        toast.success(id === "none" ? "Transition removed" : `${label} transition set`);
    };

    const setTransitionDur = (d) => {
        setClips((cs) => cs.map((c) => (c.transition ? { ...c, transition: { ...c.transition, dur: d } } : c)));
    };

    /* ---------------- overlays ---------------- */

    const addText = () => {
        const item = {
            id: uid(),
            type: "text",
            content: "Your text",
            x: 0.5,
            y: 0.5,
            size: 8,
            color: "#FFFFFF",
            font: "Outfit",
            bold: true,
            italic: false,
            outline: true,
            shadow: false,
            bg: false,
            anim: "fade",
            start: 0,
            end: totalDur || 5,
        };
        setTexts((t) => [...t, item]);
        setSelectedTextId(item.id);
        setTool("text");
    };

    const addSticker = (emoji) => {
        const item = {
            id: uid(),
            type: "sticker",
            content: emoji,
            x: 0.5,
            y: 0.4,
            size: 14,
            start: 0,
            end: totalDur || 5,
        };
        setTexts((t) => [...t, item]);
        setSelectedTextId(item.id);
    };

    const patchText = (id, patch) => {
        setTexts((ts) => ts.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    };

    const removeText = (id) => {
        setTexts((ts) => ts.filter((t) => t.id !== id));
        if (selectedTextId === id) setSelectedTextId(null);
    };

    /* ---------------- audio ---------------- */

    const onMusicFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("audio/")) {
            toast.error("Please pick an audio file (MP3, WAV, M4A…).");
            return;
        }
        const url = URL.createObjectURL(file);
        const dur = await probeMedia(url, false);
        setMusic({
            url,
            name: file.name || "music",
            duration: dur,
            volume: 0.8,
            fadeIn: 0.5,
            fadeOut: 0.5,
            start: 0,
            trimStart: 0,
            trimEnd: dur,
            projectDur: totalDur || 10,
        });
        toast.success(`${file.name || "Music"} added`);
    };

    /* ---------------- effects ---------------- */

    const toggleFav = (id) => {
        setFavEffects((f) => {
            const nf = f.includes(id) ? f.filter((x) => x !== id) : [...f, id];
            try {
                localStorage.setItem("deo_fav_effects", JSON.stringify(nf));
            } catch (e) {}
            return nf;
        });
    };

    const applyEffect = (def) => {
        if (isEffectLocked(def)) {
            toast.info(
                def.server
                    ? "This effect needs server-side processing — coming soon to Deoeditz."
                    : "Premium effect — it unlocks as soon as the premium system is enabled."
            );
            return;
        }
        setEffect({
            id: def.id,
            name: def.name,
            def,
            start: 0,
            end: totalDur || 10,
            intensity: 1,
            speed: 1,
            amount: def.amountDefault ?? 0.5,
            opacity: 1,
        });
        setFxOpen(false);
        setRecentEffects((r) => {
            const nr = [def.id, ...r.filter((x) => x !== def.id)].slice(0, 8);
            try {
                localStorage.setItem("deo_recent_effects", JSON.stringify(nr));
            } catch (e) {}
            return nr;
        });
        toast.success(`${def.name} applied`);
    };

    /* ---------------- playback ---------------- */

    const ensureClip = (at, autoplay) => {
        const v = videoRef.current;
        if (!v) return;
        v.playbackRate = live.current.speed || 1;
        const clip = at.clip;
        if (srcUrlRef.current !== clip.url) {
            srcUrlRef.current = clip.url;
            v.onloadedmetadata = () => {
                v.currentTime = clip.trimStart;
                if (autoplay) v.play().catch(() => {});
            };
            v.src = clip.url;
            v.load();
        } else {
            const want = clip.trimStart + (playheadRef.current.t - at.cum);
            if (Math.abs(v.currentTime - want) > 0.05) v.currentTime = want;
            if (autoplay) v.play().catch(() => {});
        }
        curClipRef.current = { idx: at.idx, url: clip.url, cum: at.cum };
    };

    const seekTo = (p) => {
        const cs = live.current.clips;
        const total = live.current.total;
        if (!cs || !cs.length) return;
        const P = Math.max(0, Math.min(total, p));
        playheadRef.current.t = P;
        const at = clipAt(cs, P);
        if (!at) return;
        ensureClip(at, false);
        const a = audioRef.current;
        const music = live.current.music;
        if (a && music) {
            const mEnd = music.start + (music.trimEnd - music.trimStart);
            if (P >= music.start && P < mEnd) {
                if (Math.abs(a.currentTime - (music.trimStart + (P - music.start))) > 0.3) {
                    a.currentTime = music.trimStart + (P - music.start);
                }
            }
        }
    };

    const togglePlay = () => {
        const v = videoRef.current;
        if (!v || !clips.length) return;
        if (playing) {
            v.pause();
            if (audioRef.current) audioRef.current.pause();
            playingRef.current = false;
            setPlaying(false);
        } else {
            if (playheadRef.current.t >= totalDur - 0.05) seekTo(0);
            const at = clipAt(clips, playheadRef.current.t);
            ensureClip(at, true);
            v.playbackRate = speed;
            if (audioRef.current && music) {
                audioRef.current.playbackRate = 1;
                audioRef.current.play().catch(() => {});
            }
            playingRef.current = true;
            setPlaying(true);
        }
    };

    /* ---------------- render loop ---------------- */

    useEffect(() => {
        let raf;
        let lastProg = -1;
        const loop = () => {
            raf = requestAnimationFrame(loop);
            const v = videoRef.current;
            const g = gradeCanvasRef.current;
            const disp = canvasElRef.current;
            const glc = glCanvasRef.current;
            const ov = overlayCanvasRef.current;
            const L = live.current;
            const cs = L.clips;
            if (!v || !g || !disp || !cs.length) return;
            const asp = ASPECTS.find((a) => a.id === L.aspectId) || ASPECTS[0];
            if (g.width !== asp.w) {
                g.width = asp.w;
                g.height = asp.h;
            }

            const P = playheadRef.current.t;
            const at = clipAt(cs, Math.min(P, L.total));
            if (!at) return;

            if (playingRef.current && v.readyState >= 2) {
                const clipEnd = at.clip.trimEnd;
                if (v.currentTime >= clipEnd - 0.03 || v.ended) {
                    const nxt = cs[at.idx + 1];
                    if (nxt) {
                        playheadRef.current.t = cumAt(cs, at.idx + 1);
                        const nat = clipAt(cs, playheadRef.current.t);
                        ensureClip(nat, true);
                    } else {
                        playheadRef.current.t = L.total;
                        v.pause();
                        if (audioRef.current) audioRef.current.pause();
                        playingRef.current = false;
                        setPlaying(false);
                        if (exportingRef.current && recorderRef.current && recorderRef.current.state !== "inactive") {
                            recorderRef.current.stop();
                        }
                        return;
                    }
                } else {
                    playheadRef.current.t = at.cum + (v.currentTime - at.clip.trimStart);
                }
            }

            const TP = playheadRef.current.t;
            const at2 = clipAt(cs, Math.min(TP, L.total));
            if (!at2) return;
            if (srcUrlRef.current !== at2.clip.url) ensureClip(at2, playingRef.current);

            const rot = L.rotation;
            const gctx = g.getContext("2d");
            const sHold = exportingRef.current
                ? { showOriginal: false, split: 100 }
                : { showOriginal: L.holdOriginal, split: L.split };
            drawProcessed(gctx, v, g.width, g.height, {
                css: L.css,
                intensity: L.fIntensity,
                vignette: L.vignette,
                rot,
                ...sHold,
            });
            if (!sHold.showOriginal && L.split > 0 && L.split < 100) {
                drawSplitDivider(gctx, g.width, g.height, L.split);
            }

            // active effect = user effect window or incoming clip transition
            let activeFx = null;
            const fx = L.effect;
            const local = TP - at2.cum;
            if (fx && fx.def && fx.def.body && TP >= fx.start && TP <= fx.end) activeFx = fx;
            const tr = at2.clip.transition;
            if (tr && tr.id !== "none" && local < tr.dur) {
                const tdef = FX_LIB.find((f) => f.id === tr.id);
                if (tdef && tdef.body) {
                    activeFx = { def: tdef, start: at2.cum, end: at2.cum + tr.dur, intensity: 1, speed: 1, amount: 0.5, opacity: 1 };
                }
            }
            const inWin = activeFx && TP >= activeFx.start - 0.001 && TP <= activeFx.end + 0.001;
            const needGL = (activeFx && activeFx.def.body && (inWin || exportingRef.current)) || L.advGL;
            const L2 = live.current;

            if (needGL && glc) {
                if (!engineRef.current && !engineFailedRef.current) {
                    engineRef.current = createEngine(glc);
                    if (!engineRef.current) engineFailedRef.current = true;
                }
                if (engineRef.current) {
                    if (displayRef.current !== "gl") {
                        glc.style.display = "block";
                        disp.style.display = "none";
                        displayRef.current = "gl";
                    }
                    if (glc.width !== asp.w) {
                        glc.width = asp.w;
                        glc.height = asp.h;
                    }
                    const span = Math.max(0.05, activeFx ? activeFx.end - activeFx.start : 1);
                    engineRef.current.render(activeFx ? activeFx.def.body : FX_LIB[0].body || "c = S(uv);", g, {
                        time: Math.max(0, TP - (activeFx ? activeFx.start : 0)) * (activeFx ? activeFx.speed || 1 : 1),
                        prog: Math.min(1, Math.max(0, (TP - (activeFx ? activeFx.start : 0)) / span)),
                        intensity: activeFx && inWin ? activeFx.intensity : 0,
                        speed: activeFx ? activeFx.speed || 1 : 1,
                        amount: activeFx ? activeFx.amount ?? 0.5 : 0.5,
                        opacity: activeFx ? activeFx.opacity ?? 1 : 1,
                        ...L2.glOpts,
                    });
                } else {
                    if (displayRef.current !== "2d") {
                        glc.style.display = "none";
                        disp.style.display = "block";
                        displayRef.current = "2d";
                    }
                    disp.getContext("2d").drawImage(g, 0, 0, disp.width, disp.height);
                }
            } else {
                if (displayRef.current !== "2d") {
                    if (glc) glc.style.display = "none";
                    disp.style.display = "block";
                    displayRef.current = "2d";
                }
                disp.getContext("2d").drawImage(g, 0, 0, disp.width, disp.height);
            }

            // overlays (text + stickers)
            if (ov) {
                if (ov.width !== asp.w) {
                    ov.width = asp.w;
                    ov.height = asp.h;
                }
                drawOverlay(ov.getContext("2d"), ov.width, ov.height, L.texts, TP, null);
            }

            // record composite during export
            if (exportingRef.current) {
                const rc = recordCanvasRef.current;
                if (rc.width && rc.height) {
                    const rctx = rc.getContext("2d");
                    rctx.fillStyle = "#000";
                    rctx.fillRect(0, 0, rc.width, rc.height);
                    rctx.drawImage(displayRef.current === "gl" && glc ? glc : disp, 0, 0, rc.width, rc.height);
                    if (ov) rctx.drawImage(ov, 0, 0, rc.width, rc.height);
                }
                const prog = Math.min(99, (TP / Math.max(0.1, L.total)) * 100);
                if (prog - lastProg > 1.5) {
                    lastProg = prog;
                    setExportState((st) => (st.active ? { ...st, progress: prog } : st));
                }
            }

            // music sync
            const a = audioRef.current;
            const m = L.music;
            if (a && m) {
                const mDur = m.trimEnd - m.trimStart;
                const mEnd = m.start + mDur;
                if (TP >= m.start && TP < mEnd && playingRef.current) {
                    const want = m.trimStart + (TP - m.start);
                    if (Math.abs(a.currentTime - want) > 0.35) a.currentTime = want;
                    let vol = m.volume;
                    if (m.fadeIn > 0) vol *= Math.min(1, (TP - m.start) / m.fadeIn);
                    if (m.fadeOut > 0) vol *= Math.min(1, (mEnd - TP) / m.fadeOut);
                    a.volume = Math.max(0, Math.min(1, vol));
                    if (a.paused) a.play().catch(() => {});
                } else if (!a.paused) {
                    a.pause();
                }
            }

            // playhead + time label DOM updates (no re-render)
            if (playheadRef.current.el) {
                playheadRef.current.el.style.left = `${TP * pxsRef.current}px`;
            }
            if (timeLabelRef.current) {
                timeLabelRef.current.textContent = `${fmtTime(TP)} / ${fmtTime(L.total)}`;
            }
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canvasElRef = useRef(null);
    const advGL =
        adj.exposure !== 100 ||
        adj.highlights !== 0 ||
        adj.shadows !== 0 ||
        adj.temperature !== 0 ||
        adj.tint !== 0 ||
        adj.fade !== 0 ||
        adj.sharpness !== 0;
    useEffect(() => {
        live.current.advGL = advGL;
    }, [advGL]);

    /* ---------------- filter thumbs (live previews from first clip) ---------------- */

    useEffect(() => {
        if (!clips.length) return;
        let cancelled = false;
        const tv = document.createElement("video");
        tv.muted = true;
        tv.playsInline = true;
        tv.preload = "auto";
        tv.src = clips[0].url;
        const c = document.createElement("canvas");
        c.width = 120;
        c.height = 160;
        const ctx = c.getContext("2d");
        const gen = () => {
            if (cancelled) return;
            const out = {};
            for (const f of FILTERS) {
                drawProcessed(ctx, tv, 120, 160, { css: f.css, intensity: 1, vignette: 0, showOriginal: false, split: 100 });
                out[f.id] = c.toDataURL("image/jpeg", 0.72);
            }
            if (!cancelled) setThumbs(out);
            const base = fxBaseRef.current;
            if (base) {
                base.width = 160;
                base.height = 200;
                drawProcessed(base.getContext("2d"), tv, 160, 200, { css: null, intensity: 1, vignette: 0, showOriginal: false, split: 100 });
                setFxBaseToken((x) => x + 1);
            }
        };
        tv.addEventListener("loadeddata", () => {
            tv.currentTime = Math.min(1.2, (tv.duration || 2) * 0.2);
        });
        tv.addEventListener("seeked", gen, { once: true });
        tv.load();
        return () => {
            cancelled = true;
            tv.removeAttribute("src");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clips.length && clips[0] && clips[0].url]);

    /* ---------------- captions ---------------- */

    const generateCaptions = async () => {
        if (!clips.length) {
            toast.info("Import a video first.");
            return;
        }
        setCapBusy(true);
        setCapError(null);
        try {
            const clip = clips[0];
            let blob;
            if (clip.file) blob = clip.file;
            else {
                const r = await fetch(clip.url);
                blob = await r.blob();
            }
            if (blob.size > 25 * 1024 * 1024) {
                throw new Error("This clip is over 25 MB — trim it smaller first, then generate captions.");
            }
            const fd = new FormData();
            fd.append("file", blob, clip.file ? clip.file.name || "clip.mp4" : clip.name || "clip.mp4");
            const res = await fetch(`${API}/captions/transcribe`, { method: "POST", body: fd });
            const data = await res.json();
            if (!res.ok || !data.segments) throw new Error(data.detail || "Transcription failed. Try again.");
            const segs = data.segments.filter((s) => s.text && s.text.trim());
            if (!segs.length) {
                toast.info("No speech found in this clip.");
            } else {
                const items = segs.map((sg) => ({
                    id: uid(),
                    type: "text",
                    cap: true,
                    content: sg.text.trim(),
                    x: 0.5,
                    y: 0.82,
                    size: 6,
                    color: "#FFFFFF",
                    font: "Outfit",
                    bold: true,
                    italic: false,
                    outline: true,
                    shadow: false,
                    bg: false,
                    anim: "none",
                    start: sg.start || 0,
                    end: sg.end || (sg.start || 0) + 2,
                }));
                setTexts((t) => [...t.filter((x) => !x.cap), ...items]);
                toast.success(`${items.length} captions added — edit them in the Text tab`);
            }
        } catch (e) {
            setCapError(e.message || "Could not generate captions.");
        }
        setCapBusy(false);
    };

    /* ---------------- export ---------------- */

    const pickMimeFor = (format) => {
        if (!window.MediaRecorder) return "";
        if (format === "mp4" && MediaRecorder.isTypeSupported("video/mp4")) return "video/mp4";
        const candidates = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
        return candidates.find((m) => MediaRecorder.isTypeSupported(m)) || "";
    };

    const startExport = async () => {
        const v = videoRef.current;
        if (!v || !clips.length || exportState.active) return;
        try {
            setExportState({ active: true, progress: 0, url: null, size: 0, error: null });
            exportingRef.current = true;
            v.pause();
            if (audioRef.current) audioRef.current.pause();
            playheadRef.current.t = 0;
            const at = clipAt(clips, 0);
            curClipRef.current = { idx: 0, url: null };
            srcUrlRef.current = null;
            ensureClip(at, false);
            await new Promise((resolve) => {
                const done = () => resolve();
                if (Math.abs(v.currentTime - at.clip.trimStart) < 0.05 && v.readyState >= 2) return done();
                v.onseeked = () => {
                    v.onseeked = null;
                    resolve();
                };
                setTimeout(done, 2500);
            });

            const asp = ASPECTS.find((a) => a.id === aspectId);
            const scale = Number(exportOpts.res) / Math.min(asp.w, asp.h);
            const rc = recordCanvasRef.current;
            rc.width = Math.round(asp.w * scale);
            rc.height = Math.round(asp.h * scale);
            setExportState((st) => ({ ...st, progress: 1 }));

            // let the loop composite the first real frame before recording starts
            await new Promise((r) => setTimeout(r, 450));

            const fps = exportOpts.fps;
            const stream = rc.captureStream(fps);
            try {
                v.muted = false;
                if (!audioGraphRef.current) {
                    const AC = window.AudioContext || window.webkitAudioContext;
                    const actx = new AC();
                    const vSrc = actx.createMediaElementSource(v);
                    vSrc.connect(actx.destination);
                    audioGraphRef.current = { actx, vSrc };
                }
                if (audioGraphRef.current.actx.state === "suspended") await audioGraphRef.current.actx.resume();
                const dest = audioGraphRef.current.actx.createMediaStreamDestination();
                audioGraphRef.current.vSrc.connect(dest);
                if (music && audioRef.current) {
                    if (!audioGraphRef.current.mSrc) {
                        audioGraphRef.current.mSrc = audioGraphRef.current.actx.createMediaElementSource(audioRef.current);
                    }
                    audioGraphRef.current.mSrc.connect(dest);
                }
                dest.stream.getAudioTracks().forEach((t) => stream.addTrack(t));
                audioGraphRef.current.dest = dest;
            } catch (e) {
                console.warn("audio capture unavailable", e);
            }

            const mime = pickMimeFor(exportOpts.format);
            const rec = new MediaRecorder(stream, mime ? { mimeType: mime, videoBitsPerSecond: exportOpts.res === "1080" ? 5500000 : 2800000 } : undefined);
            chunksRef.current = [];
            rec.ondataavailable = (e) => {
                if (e.data && e.data.size) chunksRef.current.push(e.data);
            };
            rec.onstop = () => {
                const type = rec.mimeType || mime || "video/webm";
                const blob = new Blob(chunksRef.current, { type });
                setExportState({ active: false, progress: 100, url: URL.createObjectURL(blob), size: blob.size, error: null, mime: type });
                toast.success("Video ready — hit download");
                exportingRef.current = false;
                if (audioGraphRef.current && audioGraphRef.current.dest) {
                    try {
                        audioGraphRef.current.vSrc.disconnect(audioGraphRef.current.dest);
                        if (audioGraphRef.current.mSrc) audioGraphRef.current.mSrc.disconnect(audioGraphRef.current.dest);
                    } catch (e) {}
                    audioGraphRef.current.dest = null;
                }
            };
            recorderRef.current = rec;
            rec.start(300);
            playingRef.current = true;
            setPlaying(true);
            v.playbackRate = speed;
            await v.play();
            if (music && audioRef.current) audioRef.current.play().catch(() => {});
        } catch (err) {
            exportingRef.current = false;
            setExportState({ active: false, progress: 0, url: null, size: 0, error: err.message || "Export failed" });
        }
    };

    const closeExport = () => {
        setExportState({ active: false, progress: 0, url: null, size: 0, error: null });
        const v = videoRef.current;
        if (v) {
            v.onended = null;
            v.pause();
        }
        exportingRef.current = false;
        seekTo(0);
    };

    /* ---------------- overlay drag ---------------- */

    const overlayPointer = (e) => {
        const ov = overlayCanvasRef.current;
        if (!ov || !texts.length) return;
        const r = ov.getBoundingClientRect();
        const px = ((e.clientX - r.left) / r.width) * ov.width;
        const py = ((e.clientY - r.top) / r.height) * ov.height;
        if (e.type === "pointerdown") {
            const hit = hitTestOverlay(texts, playheadRef.current.t, px, py, ov.width, ov.height);
            if (hit) {
                setSelectedTextId(hit);
                dragTextRef.current = { id: hit };
                ov.setPointerCapture(e.pointerId);
            } else {
                setSelectedTextId(null);
            }
        } else if (e.type === "pointermove" && dragTextRef.current) {
            const id = dragTextRef.current.id;
            const item = texts.find((t) => t.id === id);
            if (item) {
                item.x = Math.max(0.02, Math.min(0.98, px / ov.width));
                item.y = Math.max(0.02, Math.min(0.98, py / ov.height));
            }
        } else if (e.type === "pointerup" && dragTextRef.current) {
            setTexts((ts) => [...ts]);
            dragTextRef.current = null;
        }
    };

    /* ---------------- misc ---------------- */

    const goFullscreen = () => {
        const el = stageRef.current;
        if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
    };

    const renderPanel = (toolId) => {
        const selIdx = clips.findIndex((c) => c.id === selectedClipId);
        const selName = selIdx >= 0 ? clips[selIdx].name : null;
        switch (toolId) {
            case "edit":
            case "crop":
            case "rotate":
                return (
                    <EditPanel
                        hasSelection={!!selectedClipId || clips.length > 0}
                        targetName={selName}
                        onSplit={splitAtPlayhead}
                        onDelete={() => (selectedClipId ? removeClip(selectedClipId) : toast.info("Tap a clip on the timeline first."))}
                        onDuplicate={duplicateClip}
                        rotation={rotation}
                        onRotate={setRotation}
                        aspectId={aspectId}
                        onAspect={setAspectId}
                        aspects={ASPECTS}
                    />
                );
            case "audio":
                return (
                    <AudioPanel
                        music={music}
                        onFile={onMusicFile}
                        onPatch={(patch) => setMusic((m) => ({ ...m, ...patch, projectDur: totalDur || 10 }))}
                        onRemove={() => {
                            setMusic(null);
                            if (audioRef.current) audioRef.current.pause();
                        }}
                        muteOriginal={muteOriginal}
                        onMuteOriginal={setMuteOriginal}
                        fileRef={audioFileInputRef}
                    />
                );
            case "text":
                return (
                    <TextPanel
                        items={texts}
                        selectedId={selectedTextId}
                        onSelect={setSelectedTextId}
                        onAdd={addText}
                        onPatch={patchText}
                        onRemove={removeText}
                        duration={totalDur || 5}
                    />
                );
            case "effects":
                return (
                    <div className="space-y-4">
                        <button
                            data-testid="editor-effects-button"
                            onClick={() => setFxOpen(true)}
                            className="flex h-14 w-full items-center justify-center gap-2.5 rounded-2xl border border-amberx/50 bg-amberx/10 font-display text-base font-bold text-amberx"
                        >
                            <Sparkles className="h-5 w-5" />
                            Browse {FX_LIB.length} effects
                        </button>
                        {effect && (
                            <EffectControls
                                effect={effect}
                                duration={totalDur || 10}
                                onChange={(patch) => setEffect((e) => ({ ...e, ...patch }))}
                                onRemove={() => {
                                    setEffect(null);
                                    toast.success("Effect removed");
                                }}
                            />
                        )}
                    </div>
                );
            case "filters":
            case "adjust":
                return (
                    <FiltersPanel
                        filterId={filterId}
                        onFilter={setFilterId}
                        thumbs={thumbs}
                        intensity={filterIntensity}
                        onIntensity={setFilterIntensity}
                        adj={adj}
                        onAdj={(id, v) =>
                            setAdj((a) => {
                                if (id === null) return { ...DEFAULTS };
                                return { ...a, [id]: v };
                            })
                        }
                    />
                );
            case "transitions":
                return (
                    <TransitionsPanel
                        targetName={selName || (clips[0] && clips[0].name)}
                        onSet={setClipTransition}
                        transDur={(clips.find((c) => c.id === selectedClipId) || clips[0] || { transition: { dur: 0.8 } }).transition?.dur ?? 0.8}
                        onDur={setTransitionDur}
                    />
                );
            case "stickers":
            case "overlay":
                return (
                    <StickersPanel
                        items={texts}
                        selectedId={selectedTextId}
                        onSelect={setSelectedTextId}
                        onAdd={addSticker}
                        onPatch={patchText}
                        onRemove={removeText}
                        duration={totalDur || 5}
                    />
                );
            case "captions":
                return (
                    <CaptionsPanel
                        onGenerate={generateCaptions}
                        busy={capBusy}
                        error={capError}
                        captions={texts.filter((t) => t.cap)}
                        onSelect={(id) => {
                            setSelectedTextId(id);
                            setTool("text");
                        }}
                    />
                );
            case "speed":
                return <SpeedPanel speed={speed} onSpeed={setSpeed} />;
            case "export":
                return (
                    <ExportPanel
                        opts={exportOpts}
                        onOpts={setExportOpts}
                        onStart={startExport}
                        disabled={!clips.length || exportState.active}
                        mp4Ok={mp4Ok}
                    />
                );
            default:
                return null;
        }
    };

    const selText = texts.find((t) => t.id === selectedTextId);

    return (
        <div className="flex min-h-screen flex-col bg-obsidian">
            <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-white/10 bg-obsidian/90 px-3 backdrop-blur-xl sm:px-4">
                <Link to="/" data-testid="editor-home-link" aria-label="Back to Deoeditz home">
                    <Logo mark={30} />
                </Link>
                <div className="flex items-center gap-2">
                    <label
                        htmlFor="pro-import"
                        data-testid="editor-import-button"
                        className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-bold text-slate-100 hover:border-amberx hover:text-amberx"
                    >
                        <Upload className="h-4 w-4" />
                        Import video
                    </label>
                    <input
                        id="pro-import"
                        type="file"
                        accept="video/*"
                        multiple
                        data-testid="video-upload-input"
                        className="hidden"
                        onChange={(e) => {
                            addFiles(Array.from(e.target.files || []));
                            e.target.value = "";
                        }}
                    />
                    <button
                        data-testid="editor-export-button"
                        onClick={() => setTool("export")}
                        disabled={!clips.length}
                        className="inline-flex h-10 items-center gap-2 rounded-full bg-amberx px-4 font-display text-sm font-bold text-obsidian disabled:opacity-40"
                    >
                        <Download className="h-4 w-4" />
                        Export
                    </button>
                </div>
            </header>

            <input ref={fileInputRef} type="file" accept="video/*" multiple className="hidden" onChange={(e) => addFiles(Array.from(e.target.files || []))} />
            <audio ref={audioRef} src={music ? music.url : undefined} preload="auto" />

            <video
                ref={videoRef}
                className="hidden-video"
                playsInline
                muted={muted || muteOriginal}
                onLoadedMetadata={() => {}}
            />

            <main className="flex flex-1 flex-col lg:grid lg:grid-cols-[76px_minmax(0,1fr)_380px]">
                {/* desktop tool rail */}
                <aside className="order-2 hidden flex-col gap-1 border-r border-white/10 py-3 lg:order-1 lg:flex">
                    {TOOLS.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`rail-tool-${t.id}`}
                            onClick={() => (t.id === "effects" ? setFxOpen(true) : setTool(t.id))}
                            className={`mx-2 flex flex-col items-center gap-1 rounded-xl py-2.5 text-[10px] font-bold transition-colors ${
                                tool === t.id ? "bg-amberx/15 text-amberx" : "text-slate-400 hover:text-slate-100"
                            }`}
                        >
                            <t.icon className="h-5 w-5" />
                            {t.label}
                        </button>
                    ))}
                </aside>

                {/* center: preview + timeline */}
                <section className="order-1 flex min-w-0 flex-col lg:order-2">
                    <div ref={stageRef} className="relative bg-black">
                        <div className="relative mx-auto w-full max-w-[560px] lg:max-w-[640px]">
                            <div
                                data-testid="video-editor-container"
                                className={`relative overflow-hidden ${aspect.id === "9-16" ? "aspect-[9/16] max-h-[62vh]" : aspect.id === "1-1" ? "aspect-square max-h-[62vh]" : "aspect-[16/9] max-h-[62vh]"} ${aspect.id === "4-5" ? "aspect-[4/5] max-h-[62vh]" : ""}`}
                            >
                                <canvas
                                    ref={canvasElRef}
                                    data-testid="editor-canvas"
                                    width={aspect.w}
                                    height={aspect.h}
                                    className="absolute inset-0 h-full w-full"
                                />
                                <canvas
                                    ref={glCanvasRef}
                                    data-testid="editor-gl-canvas"
                                    width={aspect.w}
                                    height={aspect.h}
                                    style={{ display: "none" }}
                                    className="absolute inset-0 h-full w-full"
                                />
                                <canvas
                                    ref={overlayCanvasRef}
                                    data-testid="editor-overlay-canvas"
                                    width={aspect.w}
                                    height={aspect.h}
                                    onPointerDown={overlayPointer}
                                    onPointerMove={overlayPointer}
                                    onPointerUp={overlayPointer}
                                    className={`absolute inset-0 z-10 h-full w-full ${texts.length ? "touch-none" : "pointer-events-none"}`}
                                />
                                {!clips.length && (
                                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 bg-obsidian/95 p-6 text-center">
                                        <span className="flex h-16 w-16 items-center justify-center rounded-2xl border border-dashed border-amberx/50 text-amberx">
                                            <Film className="h-7 w-7" />
                                        </span>
                                        <p className="font-display text-xl font-bold">Import a video to start</p>
                                        <p className="text-sm text-slate-400">MP4 or WebM — it never leaves your phone.</p>
                                        <button
                                            data-testid="editor-empty-import-button"
                                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                            className="inline-flex h-12 items-center gap-2 rounded-full bg-amberx px-6 font-display text-sm font-bold text-obsidian"
                                        >
                                            <Upload className="h-4 w-4" />
                                            Import video
                                        </button>
                                        <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                                            {SAMPLES.map((s) => (
                                                <button
                                                    key={s.id}
                                                    data-testid={`video-sample-select-${s.id}`}
                                                    onClick={() => addSample(s)}
                                                    className="group relative h-14 w-20 overflow-hidden rounded-xl border border-white/15 hover:border-amberx"
                                                >
                                                    <video src={`${s.url}#t=0.8`} muted playsInline preload="metadata" className="h-full w-full object-cover" />
                                                    <span className="absolute inset-x-0 bottom-0 bg-obsidian/70 py-0.5 font-mono text-[8px] font-bold tracking-wider text-slate-200">
                                                        {s.title.toUpperCase()}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* transport */}
                    <div className="border-b border-white/10 bg-surface/40 px-3 py-2">
                        <div className="mx-auto flex max-w-3xl items-center gap-2">
                            <button
                                data-testid="editor-play-pause-button"
                                onClick={togglePlay}
                                disabled={!clips.length}
                                aria-label={playing ? "Pause" : "Play"}
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amberx text-obsidian disabled:opacity-40"
                            >
                                {playing ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}
                            </button>
                            <button
                                data-testid="editor-mute-button"
                                onClick={() => setMuted(!muted)}
                                disabled={!clips.length}
                                aria-label={muted ? "Unmute" : "Mute"}
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 text-slate-200 disabled:opacity-40"
                            >
                                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                            </button>
                            <span ref={timeLabelRef} data-testid="editor-time-display" className="shrink-0 font-mono text-xs text-slate-400">
                                00:00.0 / 00:00.0
                            </span>
                            <button
                                data-testid="before-after-toggle-button"
                                onPointerDown={() => {
                                    live.current.holdOriginal = true;
                                }}
                                onPointerUp={() => {
                                    live.current.holdOriginal = false;
                                }}
                                onPointerLeave={() => {
                                    live.current.holdOriginal = false;
                                }}
                                disabled={!clips.length}
                                className="ml-auto inline-flex h-11 shrink-0 items-center gap-2 rounded-full border border-white/15 px-4 text-xs font-bold text-slate-200 active:bg-amberx active:text-obsidian disabled:opacity-40"
                            >
                                <Eye className="h-4 w-4" />
                                <span className="hidden sm:inline">Hold for original</span>
                                <span className="sm:hidden">Original</span>
                            </button>
                            <button
                                data-testid="fullscreen-preview-button"
                                onClick={goFullscreen}
                                disabled={!clips.length}
                                aria-label="Fullscreen preview"
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 text-slate-200 disabled:opacity-40"
                            >
                                <Maximize className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="mx-auto mt-1 flex max-w-3xl items-center gap-3">
                            <span className="shrink-0 font-mono text-[10px] font-bold tracking-[0.18em] text-slate-500">
                                BEFORE/AFTER
                            </span>
                            <input
                                type="range"
                                className="slider flex-1"
                                data-testid="before-after-slider"
                                min={0}
                                max={100}
                                step={1}
                                defaultValue={100}
                                disabled={!clips.length}
                                onChange={(e) => {
                                    live.current.split = Number(e.target.value);
                                }}
                            />
                        </div>
                    </div>

                    <Timeline
                        clips={clips}
                        duration={totalDur}
                        zoom={zoom}
                        selectedId={selectedClipId}
                        playheadRef={playheadRef}
                        onSeek={seekTo}
                        onSelect={setSelectedClipId}
                        onTrim={trimClip}
                        onReorder={reorderClip}
                        onSplit={splitAtPlayhead}
                        onDelete={() => selectedClipId && removeClip(selectedClipId)}
                        onDuplicate={duplicateClip}
                    />

                    {/* timeline zoom */}
                    <div className="flex items-center gap-3 border-b border-white/10 bg-surface/40 px-4 py-1.5">
                        <span className="font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">ZOOM</span>
                        <input
                            type="range"
                            className="slider flex-1"
                            data-testid="timeline-zoom-slider"
                            min={0.5}
                            max={4}
                            step={0.1}
                            value={zoom}
                            disabled={!clips.length}
                            onChange={(e) => setZoom(Number(e.target.value))}
                        />
                    </div>

                    {/* mobile toolbar */}
                    <div className="no-scrollbar flex gap-2 overflow-x-auto border-b border-white/10 bg-obsidian px-3 py-2.5 lg:hidden">
                        {TOOLS.map((t) => (
                            <button
                                key={t.id}
                                data-testid={`tool-${t.id}`}
                                onClick={() => (t.id === "effects" ? setFxOpen(true) : setTool(t.id))}
                                className={`flex h-11 shrink-0 items-center gap-1.5 rounded-full border px-4 text-xs font-bold ${
                                    tool === t.id ? "border-amberx bg-amberx/10 text-amberx" : "border-white/15 text-slate-200"
                                }`}
                            >
                                <t.icon className="h-4 w-4" />
                                {t.label}
                            </button>
                        ))}
                    </div>
                </section>

                {/* desktop properties panel */}
                <aside className="order-3 hidden overflow-y-auto border-l border-white/10 p-4 lg:block">
                    <p className="mb-3 font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">
                        {(SHEET_TITLES[tool || "edit"] || "PROPERTIES").toUpperCase()}
                    </p>
                    {renderPanel(tool || "edit")}
                </aside>
            </main>

            {/* mobile bottom sheet */}
            {tool && tool !== "effects" && (
                <div className="fixed inset-0 z-50 flex flex-col justify-end lg:hidden" data-testid="editor-sheet">
                    <button aria-label="Close panel" className="flex-1 bg-obsidian/60" onClick={() => setTool(null)} />
                    <div className="max-h-[72vh] overflow-y-auto rounded-t-3xl border-t border-white/10 bg-obsidian p-4">
                        <div className="mb-3 flex items-center justify-between">
                            <p className="font-display text-base font-bold text-slate-100">{SHEET_TITLES[tool]}</p>
                            <button
                                data-testid="sheet-close-button"
                                onClick={() => setTool(null)}
                                aria-label="Close"
                                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-200"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        {renderPanel(tool)}
                    </div>
                </div>
            )}

            <EffectsLibrary
                open={fxOpen}
                onClose={() => setFxOpen(false)}
                baseFrame={fxBaseRef.current}
                baseToken={fxBaseToken}
                activeId={effect ? effect.id : null}
                favorites={favEffects}
                recents={recentEffects}
                onApply={applyEffect}
                onToggleFav={toggleFav}
            />

            {(exportState.active || exportState.url || exportState.error) && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-obsidian/90 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-surface p-6 text-center">
                        {exportState.active && (
                            <>
                                <Loader2 className="mx-auto h-10 w-10 animate-spin text-amberx" />
                                <p className="mt-4 font-display text-xl font-bold">Rendering your video…</p>
                                <p className="mt-1 text-sm text-slate-400">
                                    {exportOpts.res}p · {exportOpts.fps}fps · keep this tab open. {Math.round(exportState.progress)}%
                                </p>
                                <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-white/10">
                                    <div className="h-full rounded-full bg-amberx transition-[width] duration-200" style={{ width: `${exportState.progress}%` }} />
                                </div>
                            </>
                        )}
                        {exportState.url && (
                            <>
                                <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
                                <p className="mt-4 font-display text-xl font-bold">Your video is ready</p>
                                <video src={exportState.url} controls playsInline className="mt-4 max-h-56 w-full rounded-2xl border border-white/10 bg-black" />
                                <p className="mt-2 font-mono text-[11px] text-slate-500">{(exportState.size / (1024 * 1024)).toFixed(1)} MB</p>
                                <a
                                    data-testid="export-download-link"
                                    href={exportState.url}
                                    download={`deoeditz-video.${(exportState.mime || "").includes("mp4") ? "mp4" : "webm"}`}
                                    className="mt-4 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-amberx font-display text-sm font-bold text-obsidian"
                                >
                                    <Download className="h-4 w-4" />
                                    Download video
                                </a>
                                <button
                                    data-testid="export-close-button"
                                    onClick={closeExport}
                                    className="mt-2 inline-flex h-11 w-full items-center justify-center rounded-full border border-white/15 text-sm font-bold text-slate-200"
                                >
                                    Back to editing
                                </button>
                            </>
                        )}
                        {exportState.error && (
                            <>
                                <X className="mx-auto h-10 w-10 text-emberx" />
                                <p className="mt-4 font-display text-xl font-bold">Export failed</p>
                                <p className="mt-1 text-sm text-slate-400">{exportState.error}</p>
                                <button
                                    data-testid="export-close-button"
                                    onClick={closeExport}
                                    className="mt-4 inline-flex h-11 w-full items-center justify-center rounded-full border border-white/15 text-sm font-bold text-slate-200"
                                >
                                    Close
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
